import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import {
  createBrowserRouter,
  matchRoutes,
  RouterProvider,
} from "react-router-dom";

/* Every stylesheet in the app is imported here, in the order main.css has
   always had them. Left to the lazily loaded pages below, webpack would split
   them into per-page CSS files that load after main.css - changing which rule
   wins wherever two of them conflict. */
import 'intl-tel-input/build/css/intlTelInput.css';
import './components/intlTelInputOverrides.css';
import './container/PortfolioUnified/PortfolioUnified.css';
import './index.css';
import './App.css';

import Navbar from './components/Navbar/Navbar';
import FindUs from './container/Findus/FindUs';
import WhatsAppButton from './components/WhatsAppButton/WhatsAppButton';
import { pageview, initCopyTracking } from './utils/analytics';

/* ── Routes ──────────────────────────────────────────────────────────────
   Every page is its own chunk, fetched when its route is first matched.
   Previously all of them - the homepage, seven service pages and every
   article - were bundled into main.js, so each visit downloaded and ran the
   whole site to render one page. On a throttled mobile connection that
   bundle sat on the LCP critical path, which is most of why mobile scored in
   the 60s while desktop, with bandwidth and CPU to spare, did not.

   The chrome shared by every page (navbar, footer, WhatsApp button) stays in
   main.js. */

let router;

/* A tab opened before a deploy still asks for page chunks by the hashed names
   of that build, and the deploy deletes them (`mirror --delete`). Rather than
   fail the navigation with React Router's error screen, load the destination
   as a full page, which picks up the new build. On the initial load there is
   no navigation to redirect, so the error propagates and the prerendered page
   simply stays as it is. */
const page = (load, layout = (Page) => <Page />) => async () => {
  try {
    const { default: Page } = await load();
    return { element: layout(Page) };
  } catch (error) {
    const target = router && router.state.navigation.location;
    if (!target) throw error;
    window.location.assign(target.pathname + target.search + target.hash);
    return new Promise(() => {}); // keep the current page up until the browser leaves
  }
};

// Service, location and hub pages sit on the dark theme background.
const darkLayout = (Page) => (
  <div className="bg-primary min-h-screen w-full">
    <Navbar />
    <Page />
    <FindUs />
  </div>
);

// Articles and the projects page bring their own backgrounds.
const plainLayout = (Page) => (
  <>
    <Navbar />
    <Page />
    <FindUs />
  </>
);

const routes = [
  // Main homepage (no nav/footer from the blog layout)
  { path: "/", lazy: page(() => import('./App')) },
  { path: "/contact", lazy: page(() => import('./container/ContactPage/ContactPage')) },

  // PROJECT ROUTE
  { path: "/projects", lazy: page(() => import('./container/Projects/ProjectsPage'), plainLayout) },

  // SERVICE ROUTES
  { path: "/services", lazy: page(() => import('./container/ServicePages/ServicesIndex'), darkLayout) },
  { path: "/services/web-mobile-development", lazy: page(() => import('./container/ServicePages/WebMobileDevelopment'), darkLayout) },
  { path: "/services/ai-automation", lazy: page(() => import('./container/ServicePages/AiAutomation'), darkLayout) },
  { path: "/services/custom-operations-software", lazy: page(() => import('./container/ServicePages/CustomOperationsSoftware'), darkLayout) },
  { path: "/services/embedded-systems", lazy: page(() => import('./container/ServicePages/EmbeddedSystems'), darkLayout) },
  { path: "/services/medical-software-development", lazy: page(() => import('./container/ServicePages/MedicalSoftwareDevelopment'), darkLayout) },
  { path: "/services/generative-engine-optimization", lazy: page(() => import('./container/ServicePages/GenerativeEngineOptimization'), darkLayout) },
  { path: "/services/uae-e-invoicing-integration", lazy: page(() => import('./container/ServicePages/UaeEInvoicingIntegration'), darkLayout) },

  // LOCATION ROUTE
  { path: "/software-development-dubai", lazy: page(() => import('./container/LocationPages/SoftwareDevelopmentDubai'), darkLayout) },

  // BLOG ROUTES
  { path: "/blogs", lazy: page(() => import('./container/Blogs/BlogsPage'), plainLayout) },
  { path: "/blogs/pint-ae-uae-e-invoicing", lazy: page(() => import('./container/Blogs/PintAeUaeEInvoicing'), plainLayout) },
  { path: "/blogs/ai-integration-business", lazy: page(() => import('./container/Blogs/AIIntegrationBusiness'), plainLayout) },
  { path: "/blogs/custom-software-healthcare", lazy: page(() => import('./container/Blogs/CustomSoftwareHealthcare'), plainLayout) },
  { path: "/blogs/robotics-warehouse-automation", lazy: page(() => import('./container/Blogs/RoboticsWarehouseAutomation'), plainLayout) },
  { path: "/blogs/smart-home-problems", lazy: page(() => import('./container/Blogs/SmartHomeProblems'), plainLayout) },
  { path: "/blogs/generative-engine-optimization", lazy: page(() => import('./container/Blogs/GenerativeEngineOptimization'), plainLayout) },
  { path: "/blogs/in-flight-software-modernization", lazy: page(() => import('./container/Blogs/InFlightSoftwareModernization'), plainLayout) },

  { path: "/404", lazy: page(() => import('./container/NotFound/NotFound')) },
  { path: "*", lazy: page(() => import('./container/NotFound/NotFound')) },
];

/* A prerendered page has to hydrate against exactly the markup it was
   snapshotted with. If its route were still lazy when React started, the
   router would render nothing while the chunk loaded, React would treat that
   as a mismatch, discard the prerendered DOM and rebuild the page from
   scratch. So the chunk for the URL being loaded is fetched first, and React
   only starts once the page component is in hand - the pattern React Router
   documents for hydrating lazy routes. Later navigations fetch on demand,
   keeping the current page on screen until the next one is ready. */
const loadMatchedRoutes = () =>
  Promise.all(
    (matchRoutes(routes, window.location) || [])
      .filter((match) => match.route.lazy)
      .map(async ({ route }) => {
        Object.assign(route, await route.lazy(), { lazy: undefined });
      }),
  );

/* Analytics page views.
   gtag.js is configured with send_page_view:false (see public/index.html), so
   this is the only thing that reports a page view - which means no double
   counting on load, and every client-side route change is recorded.

   The short delay lets react-helmet-async flush the new <title> first;
   without it every navigation would be logged under the previous page's title. */
const sendPageview = (path) => {
  setTimeout(() => pageview(path), 120);
};

loadMatchedRoutes().then(() => {
  router = createBrowserRouter(routes);

  // One page view per completed navigation. Navigating to a page whose chunk
  // is not loaded yet first publishes a "loading" state that still carries the
  // old location; counting that would log the page being left a second time.
  let lastKey = router.state.location.key;
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle' || state.location.key === lastKey) return;
    lastKey = state.location.key;
    sendPageview(state.location.pathname);
  });

  const rootElement = document.getElementById('root');
  const app = (
    <HelmetProvider>
      <RouterProvider router={router} />
      <WhatsAppButton />
    </HelmetProvider>
  );

  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
});

// Initial page view - router.subscribe only fires on subsequent navigations.
sendPageview(window.location.pathname);
initCopyTracking();
